'use client'

import React, { useEffect, useRef, useMemo } from 'react'
import { IChartApi } from 'lightweight-charts'
import type { 
  PatternDrawing,
  HeadAndShouldersPattern,
  DoubleTopBottomPattern,
  TrianglePattern,
  WedgePattern,
  FlagPennantPattern,
  PatternPoint
} from '../types/drawing'

// Import pattern components
import HeadAndShoulders from './drawings/HeadAndShoulders'
import DoubleTopBottom from './drawings/DoubleTopBottom'
import Triangle from './drawings/Triangle'
import PatternNotification from './PatternNotification'

interface PatternManagerProps {
  chart: IChartApi
  patterns: Map<string, PatternDrawing>
  onPatternUpdate?: (pattern: PatternDrawing) => void
  onPatternDelete?: (patternId: string) => void
  isInteractive?: boolean
  currentPrice?: number
  onShowNotification?: (patterns: PatternDrawing[]) => void
}

const PatternManager: React.FC<PatternManagerProps> = ({
  chart,
  patterns,
  onPatternUpdate,
  onPatternDelete,
  isInteractive = true,
  currentPrice = 0,
  onShowNotification
}) => {
  const [notification, setNotification] = React.useState<{ patterns: PatternDrawing[], currentPrice: number } | null>(null)

  // Render pattern components based on type
  const renderPattern = (pattern: PatternDrawing) => {
    switch (pattern.type) {
      case 'head-and-shoulders':
        return (
          <HeadAndShoulders
            key={pattern.id}
            chart={chart}
            pattern={pattern as HeadAndShouldersPattern}
            onUpdate={onPatternUpdate}
            onDelete={onPatternDelete}
          />
        )
      
      case 'double-top':
      case 'double-bottom':
        return (
          <DoubleTopBottom
            key={pattern.id}
            chart={chart}
            pattern={pattern as DoubleTopBottomPattern}
            onUpdate={onPatternUpdate}
            onDelete={onPatternDelete}
          />
        )
      
      case 'ascending-triangle':
      case 'descending-triangle':
      case 'symmetrical-triangle':
        return (
          <Triangle
            key={pattern.id}
            chart={chart}
            pattern={pattern as TrianglePattern}
            onUpdate={onPatternUpdate}
            onDelete={onPatternDelete}
          />
        )
      
      // Placeholder for other patterns (to be implemented)
      case 'rising-wedge':
      case 'falling-wedge':
        // TODO: Implement Wedge component
        return null
      
      case 'bull-flag':
      case 'bear-flag':
      case 'pennant':
        // TODO: Implement FlagPennant component
        return null
      
      default:
        return null
    }
  }

  const showNotification = (detectedPatterns: PatternDrawing[]) => {
    setNotification({
      patterns: detectedPatterns,
      currentPrice: currentPrice
    })
  }

  return (
    <div className="pattern-manager">
      {Array.from(patterns.values())
        .filter(pattern => pattern.isVisible)
        .map(pattern => renderPattern(pattern))
      }
      {/* Pattern Detection Notification */}
      {notification && (
        <PatternNotification
          patterns={notification.patterns}
          currentPrice={notification.currentPrice}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  )
}

export default PatternManager

// Pattern creation utilities
export const createPatternFromPoints = (
  type: PatternDrawing['type'],
  points: PatternPoint[],
  id?: string
): PatternDrawing | null => {
  const patternId = id || `pattern_${type}_${Date.now()}`

  try {
    switch (type) {
      case 'head-and-shoulders': {
        if (points.length < 5) return null
        
        const [leftShoulder, leftValley, head, rightValley, rightShoulder] = points
        
        return {
          id: patternId,
          type: 'head-and-shoulders',
          points: points.map(p => ({ time: p.time, price: p.price })),
          style: {
            lineColor: '#F23645',
            lineWidth: 2,
            lineStyle: 'solid',
            textColor: '#E0E3EB',
            fontSize: 12
          },
          isSelected: false,
          isVisible: true,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          zIndex: 10,
          patternPoints: {
            leftShoulder: { ...leftShoulder, role: 'peak', significance: 0.7 },
            head: { ...head, role: 'peak', significance: 1.0 },
            rightShoulder: { ...rightShoulder, role: 'peak', significance: 0.7 },
            leftValley: { ...leftValley, role: 'valley', significance: 0.8 },
            rightValley: { ...rightValley, role: 'valley', significance: 0.8 },
            neckline: [
              { ...leftValley, role: 'neckline', significance: 0.8 },
              { ...rightValley, role: 'neckline', significance: 0.8 }
            ]
          },
          isInverted: false,
          confirmation: false
        } as HeadAndShouldersPattern
      }

      case 'double-top':
      case 'double-bottom': {
        if (points.length < 3) return null
        
        const [firstPeak, valley, secondPeak] = points
        const patternColor = type === 'double-top' ? '#FF9800' : '#4CAF50'
        
        return {
          id: patternId,
          type,
          points: points.map(p => ({ time: p.time, price: p.price })),
          style: {
            lineColor: patternColor,
            lineWidth: 2,
            lineStyle: 'solid',
            textColor: '#E0E3EB',
            fontSize: 12
          },
          isSelected: false,
          isVisible: true,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          zIndex: 10,
          patternPoints: {
            firstPeak: { ...firstPeak, role: type === 'double-top' ? 'resistance' : 'support', significance: 0.9 },
            secondPeak: { ...secondPeak, role: type === 'double-top' ? 'resistance' : 'support', significance: 0.9 },
            valley: { ...valley, role: type === 'double-top' ? 'support' : 'resistance', significance: 0.7 },
            neckline: [
              { 
                time: firstPeak.time, 
                price: valley.price, // This is valley for double-top (support), peak for double-bottom (resistance)
                role: 'neckline', 
                significance: 0.8 
              },
              { 
                time: secondPeak.time, 
                price: valley.price, // This is valley for double-top (support), peak for double-bottom (resistance)
                role: 'neckline', 
                significance: 0.8 
              }
            ]
          },
          tolerance: 0.03,
          confirmation: false
        } as DoubleTopBottomPattern
      }

      case 'ascending-triangle':
      case 'descending-triangle':
      case 'symmetrical-triangle': {
        if (points.length < 4) return null
        
        // Separate highs and lows
        const sortedPoints = [...points].sort((a, b) => a.time - b.time)
        const highs = sortedPoints.filter((_, index) => index % 2 === 0)
        const lows = sortedPoints.filter((_, index) => index % 2 === 1)
        
        if (highs.length < 2 || lows.length < 2) return null
        
        const patternColor = type === 'ascending-triangle' ? '#2196F3' :
                            type === 'descending-triangle' ? '#F23645' : '#9C27B0'
        
        // Calculate convergence point (simplified)
        const firstHigh = highs[0]
        const lastHigh = highs[highs.length - 1]
        const firstLow = lows[0]
        const lastLow = lows[lows.length - 1]
        
        const upperSlope = (lastHigh.price - firstHigh.price) / (lastHigh.time - firstHigh.time)
        const lowerSlope = (lastLow.price - firstLow.price) / (lastLow.time - firstLow.time)
        
        const convergenceTime = (firstLow.price - firstHigh.price + upperSlope * firstHigh.time - lowerSlope * firstLow.time) / (upperSlope - lowerSlope)
        const convergencePrice = firstHigh.price + upperSlope * (convergenceTime - firstHigh.time)
        
        return {
          id: patternId,
          type,
          points: points.map(p => ({ time: p.time, price: p.price })),
          style: {
            lineColor: patternColor,
            lineWidth: 2,
            lineStyle: 'solid',
            textColor: '#E0E3EB',
            fontSize: 12
          },
          isSelected: false,
          isVisible: true,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          zIndex: 10,
          patternPoints: {
            highs: highs.map(h => ({ ...h, role: 'resistance', significance: 0.8 })),
            lows: lows.map(l => ({ ...l, role: 'support', significance: 0.8 })),
            convergencePoint: { time: convergenceTime, price: convergencePrice }
          },
          upperTrendline: [firstHigh, lastHigh],
          lowerTrendline: [firstLow, lastLow],
          confirmation: false,
          duration: (lastHigh.time - firstHigh.time) / 86400 // days
        } as TrianglePattern
      }

      default:
        return null
    }
  } catch (error) {
    console.error(`Error creating ${type} pattern:`, error)
    return null
  }
}

// Pattern detection utilities
export const detectPatterns = (
  chartData: Array<{ time: number, open: number, high: number, low: number, close: number }>,
  minConfidence: number = 0.7
): PatternDrawing[] => {
  const patterns: PatternDrawing[] = []
  
  if (chartData.length < 20) return patterns
  
  console.log(`🔍 Starting pattern detection on ${chartData.length} candles with ${minConfidence * 100}% minimum confidence...`)
  
  try {
    // Detect Double Top patterns
    const doubleTopPatterns = detectDoubleTopPatterns(chartData, minConfidence)
    patterns.push(...doubleTopPatterns)
    console.log(`📊 Found ${doubleTopPatterns.length} double top patterns`)
    
    // Detect Double Bottom patterns
    const doubleBottomPatterns = detectDoubleBottomPatterns(chartData, minConfidence)
    patterns.push(...doubleBottomPatterns)
    console.log(`📊 Found ${doubleBottomPatterns.length} double bottom patterns`)
    
    // Detect Head and Shoulders patterns
    const headAndShouldersPatterns = detectHeadAndShouldersPatterns(chartData, minConfidence)
    patterns.push(...headAndShouldersPatterns)
    console.log(`📊 Found ${headAndShouldersPatterns.length} head and shoulders patterns`)
    
    // Detect Triangle patterns
    const trianglePatterns = detectTrianglePatterns(chartData, minConfidence)
    patterns.push(...trianglePatterns)
    console.log(`📊 Found ${trianglePatterns.length} triangle patterns`)
    
    console.log(`✅ Pattern detection complete: ${patterns.length} total patterns found`)
    
  } catch (error) {
    console.error('❌ Error detecting patterns:', error)
  }
  
  return patterns
}

// Helper functions for pattern detection
const detectDoubleTopPatterns = (data: any[], minConfidence: number): DoubleTopBottomPattern[] => {
  const patterns: DoubleTopBottomPattern[] = []
  const peaks = findPeaks(data, 'high')
  
  for (let i = 0; i < peaks.length - 1; i++) {
    for (let j = i + 1; j < peaks.length; j++) {
      const peak1 = peaks[i]
      const peak2 = peaks[j]
      
      // Check if peaks are similar in height (within 3% tolerance)
      const priceDiff = Math.abs(peak1.price - peak2.price)
      const avgPrice = (peak1.price + peak2.price) / 2
      const similarity = 1 - (priceDiff / avgPrice)
      
      if (similarity >= minConfidence) {
        // Find valley between peaks
        const valleyData = data.slice(peak1.index, peak2.index + 1)
        const valleyIndex = valleyData.reduce((minIdx, candle, idx) => 
          candle.low < valleyData[minIdx].low ? idx : minIdx, 0)
        const valley = {
          time: valleyData[valleyIndex].time,
          price: valleyData[valleyIndex].low,
          role: 'support' as const,
          significance: 0.7
        }
        
        const pattern = createPatternFromPoints('double-top', [
          { time: peak1.time, price: peak1.price, role: 'resistance', significance: 0.9 },
          valley,
          { time: peak2.time, price: peak2.price, role: 'resistance', significance: 0.9 }
        ])
        
        if (pattern) patterns.push(pattern as DoubleTopBottomPattern)
      }
    }
  }
  
  return patterns
}

const detectDoubleBottomPatterns = (data: any[], minConfidence: number): DoubleTopBottomPattern[] => {
  const patterns: DoubleTopBottomPattern[] = []
  const valleys = findPeaks(data, 'low')
  
  for (let i = 0; i < valleys.length - 1; i++) {
    for (let j = i + 1; j < valleys.length; j++) {
      const valley1 = valleys[i]
      const valley2 = valleys[j]
      
      // Check if valleys are similar in depth
      const priceDiff = Math.abs(valley1.price - valley2.price)
      const avgPrice = (valley1.price + valley2.price) / 2
      const similarity = 1 - (priceDiff / avgPrice)
      
      if (similarity >= minConfidence) {
        // Find peak between valleys
        const peakData = data.slice(valley1.index, valley2.index + 1)
        const peakIndex = peakData.reduce((maxIdx, candle, idx) => 
          candle.high > peakData[maxIdx].high ? idx : maxIdx, 0)
        const peak = {
          time: peakData[peakIndex].time,
          price: peakData[peakIndex].high,
          role: 'resistance' as const,
          significance: 0.7
        }
        
        const pattern = createPatternFromPoints('double-bottom', [
          { time: valley1.time, price: valley1.price, role: 'support', significance: 0.9 },
          peak,
          { time: valley2.time, price: valley2.price, role: 'support', significance: 0.9 }
        ])
        
        if (pattern) patterns.push(pattern as DoubleTopBottomPattern)
      }
    }
  }
  
  return patterns
}

const detectHeadAndShouldersPatterns = (data: any[], minConfidence: number): HeadAndShouldersPattern[] => {
  const patterns: HeadAndShouldersPattern[] = []
  const peaks = findPeaks(data, 'high', 7) // Wider window for more significant peaks
  
  if (peaks.length < 3) return patterns
  
  // Look for H&S patterns (need 3 consecutive peaks where middle is highest)
  for (let i = 0; i < peaks.length - 2; i++) {
    const leftShoulder = peaks[i]
    const head = peaks[i + 1]
    const rightShoulder = peaks[i + 2]
    
    // Check if middle peak is higher (head)
    if (head.price > leftShoulder.price && head.price > rightShoulder.price) {
      // Check if shoulders are roughly equal
      const shoulderDiff = Math.abs(leftShoulder.price - rightShoulder.price)
      const avgShoulderPrice = (leftShoulder.price + rightShoulder.price) / 2
      const shoulderSimilarity = 1 - (shoulderDiff / avgShoulderPrice)
      
      if (shoulderSimilarity >= minConfidence) {
        // Find valleys between peaks
        const leftValleyData = data.slice(leftShoulder.index, head.index)
        const rightValleyData = data.slice(head.index, rightShoulder.index)
        
        const leftValleyIdx = leftValleyData.reduce((minIdx, candle, idx) => 
          candle.low < leftValleyData[minIdx].low ? idx : minIdx, 0)
        const rightValleyIdx = rightValleyData.reduce((minIdx, candle, idx) => 
          candle.low < rightValleyData[minIdx].low ? idx : minIdx, 0)
        
        const leftValley = {
          time: leftValleyData[leftValleyIdx].time,
          price: leftValleyData[leftValleyIdx].low,
          role: 'valley' as const,
          significance: 0.8
        }
        
        const rightValley = {
          time: rightValleyData[rightValleyIdx].time,
          price: rightValleyData[rightValleyIdx].low,
          role: 'valley' as const,
          significance: 0.8
        }
        
        const pattern = createPatternFromPoints('head-and-shoulders', [
          { time: leftShoulder.time, price: leftShoulder.price, role: 'peak', significance: 0.7 },
          leftValley,
          { time: head.time, price: head.price, role: 'peak', significance: 1.0 },
          rightValley,
          { time: rightShoulder.time, price: rightShoulder.price, role: 'peak', significance: 0.7 }
        ])
        
        if (pattern) patterns.push(pattern as HeadAndShouldersPattern)
      }
    }
  }
  
  return patterns
}

const detectTrianglePatterns = (data: any[], minConfidence: number): TrianglePattern[] => {
  const patterns: TrianglePattern[] = []
  const peaks = findPeaks(data, 'high', 5)
  const valleys = findPeaks(data, 'low', 5)
  
  if (peaks.length < 2 || valleys.length < 2) return patterns
  
  // Look for ascending triangles (flat resistance, rising support)
  for (let i = 0; i < peaks.length - 1; i++) {
    for (let j = i + 1; j < peaks.length; j++) {
      const peak1 = peaks[i]
      const peak2 = peaks[j]
      
      // Check if peaks are roughly at same level (flat resistance)
      const peakDiff = Math.abs(peak1.price - peak2.price)
      const avgPeakPrice = (peak1.price + peak2.price) / 2
      const peakSimilarity = 1 - (peakDiff / avgPeakPrice)
      
      if (peakSimilarity >= minConfidence) {
        // Find valleys between these peaks that show rising trend
        const valleysBetween = valleys.filter(v => v.time > peak1.time && v.time < peak2.time)
        
        if (valleysBetween.length >= 1) {
          // Check if valleys are trending upward
          const firstValley = valleys.find(v => v.time < peak1.time)
          const lastValley = valleysBetween[valleysBetween.length - 1]
          
          if (firstValley && lastValley.price > firstValley.price) {
            // We have an ascending triangle pattern
            const highs = [peak1, peak2].map(p => ({ 
              time: p.time, 
              price: p.price, 
              role: 'resistance' as const, 
              significance: 0.8 
            }))
            const lows = [firstValley, lastValley].map(v => ({ 
              time: v.time, 
              price: v.price, 
              role: 'support' as const, 
              significance: 0.8 
            }))
            
            const pattern = createPatternFromPoints('ascending-triangle', [...highs, ...lows])
            if (pattern) patterns.push(pattern as TrianglePattern)
          }
        }
      }
    }
  }
  
  return patterns
}

const findPeaks = (data: any[], type: 'high' | 'low', window: number = 5) => {
  const peaks = []
  const priceKey = type
  const comparison = type === 'high' ? 
    (a: number, b: number) => a > b : 
    (a: number, b: number) => a < b
  
  for (let i = window; i < data.length - window; i++) {
    const currentPrice = data[i][priceKey]
    let isPeak = true
    
    // Check if current point is higher/lower than surrounding points
    for (let j = i - window; j <= i + window; j++) {
      if (j !== i && !comparison(currentPrice, data[j][priceKey])) {
        isPeak = false
        break
      }
    }
    
    if (isPeak) {
      peaks.push({
        index: i,
        time: data[i].time,
        price: currentPrice
      })
    }
  }
  
  return peaks
} 